import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Typography, Switch, Divider } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import { Formik, Form, FormikProps } from 'formik';
import i18n from 'i18n-js';

import { useStyles } from '../styles';
import { useAppSelector } from 'index';
import restaurantDetailsStore from 'store/restaurantDetailsStore';

import FormikTimePicker from 'components/shared/components/FormikTimePicker';

export interface AutomationTabHandle {
    submitForm: () => void;
}

interface AutomationTabProps {
    onDirtyChange?: (isDirty: boolean) => void;
}

interface FormValues {
    isEnabled: boolean;
    // Encoded the same way as FormikTimePicker: hours + minutes / 100.
    generationTime: number;
}

interface AutomationPreview {
    nextGenerationDate: number;
    nextGenerationBusinessDay: number;
}

const DEFAULT_TIME_ZONE = 'Europe/Zurich';
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

function toDecimalTime(hour: number, minute: number) {
    return hour + minute / 100;
}

function fromDecimalTime(value: number) {
    const hour = Math.floor(value);
    const minute = Math.round((value - hour) * 100);
    return { hour, minute };
}

// Reads "now" in the restaurant's timezone and re-labels it as UTC, matching the "localized ms"
// convention the backend uses for these timestamps (wall-clock value read back with timeZone: 'UTC').
function getTimeZoneNowParts(timeZone: string) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    }).formatToParts(new Date());
    const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
    const hour = get('hour');
    return {
        year: get('year'),
        month: get('month'),
        day: get('day'),
        hour: hour === 24 ? 0 : hour,
        minute: get('minute'),
    };
}

// Pure client-side equivalent of DateTimeHelper.CalculateNextAutomaticClosing - no API round-trip needed.
function computeAutomationPreview(
    isEnabled: boolean,
    generationHour: number,
    generationMinute: number,
    timeZone: string
): AutomationPreview | null {
    if (!isEnabled) return null;

    const { year, month, day, hour, minute } = getTimeZoneNowParts(timeZone);
    const nowMs = Date.UTC(year, month - 1, day, hour, minute);
    const todayGenerationMs = Date.UTC(year, month - 1, day, generationHour, generationMinute, 0);

    const nextGenerationDate =
        nowMs < todayGenerationMs ? todayGenerationMs : todayGenerationMs + ONE_DAY_MS;
    const businessDay = new Date(nextGenerationDate - ONE_DAY_MS);
    const nextGenerationBusinessDay = Date.UTC(
        businessDay.getUTCFullYear(),
        businessDay.getUTCMonth(),
        businessDay.getUTCDate()
    );

    return { nextGenerationDate, nextGenerationBusinessDay };
}

// Preview timestamps use the same "localized" encoding as the rest of the app (wall-clock time read as UTC).
function formatBusinessDay(ms: number, language: string) {
    return new Intl.DateTimeFormat(language === 'de' ? 'de-CH' : 'en-GB', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
    }).format(new Date(ms));
}

function formatGenerationTime(ms: number) {
    return new Intl.DateTimeFormat('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'UTC',
    }).format(new Date(ms));
}

// Reports Formik's dirty flag to the parent so the external save button can be disabled/enabled.
function DirtyReporter({
    dirty,
    onDirtyChange,
}: {
    dirty: boolean;
    onDirtyChange?: (isDirty: boolean) => void;
}) {
    useEffect(() => {
        onDirtyChange?.(dirty);
    }, [dirty, onDirtyChange]);
    return null;
}

const AutomationTab = forwardRef<AutomationTabHandle, AutomationTabProps>(
    ({ onDirtyChange }, ref) => {
        const classes = useStyles();
        const dispatch = useDispatch();
        const language = useAppSelector((state) => state.global.language);
        const restaurant = useAppSelector((state) => state.restaurantDetails.restaurantInformation);
        const settings = restaurant?.automaticZClosingSettings;
        const timeZone = restaurant?.timeZone || DEFAULT_TIME_ZONE;

        const formikRef = useRef<FormikProps<FormValues>>(null);

        useImperativeHandle(ref, () => ({
            submitForm: () => formikRef.current?.submitForm(),
        }));

        return (
            <Formik<FormValues>
                innerRef={formikRef}
                initialValues={{
                    isEnabled: settings?.isEnabled || false,
                    generationTime: toDecimalTime(
                        settings?.generationHour ?? 4,
                        settings?.generationMinute ?? 0
                    ),
                }}
                enableReinitialize
                onSubmit={(values, { setSubmitting }) => {
                    const { hour, minute } = fromDecimalTime(values.generationTime);
                    dispatch(
                        restaurantDetailsStore.actionCreators.updateAutomaticZClosingSettings({
                            isEnabled: values.isEnabled,
                            generationHour: hour,
                            generationMinute: minute,
                        })
                    );
                    // The thunk reports progress via the global spinner/toaster, not a returned promise.
                    setSubmitting(false);
                }}
            >
                {({ values, setFieldValue, isSubmitting, dirty }) => {
                    const { hour, minute } = fromDecimalTime(values.generationTime);
                    const preview = computeAutomationPreview(
                        values.isEnabled,
                        hour,
                        minute,
                        timeZone
                    );

                    return (
                        <>
                            <DirtyReporter dirty={dirty} onDirtyChange={onDirtyChange} />
                            <Form>
                                <fieldset
                                    disabled={isSubmitting}
                                    className={classes.automationFieldset}
                                >
                                    <Box className={classes.automationPanelsContainer}>
                                        <Box className={classes.automationLeftPanel}>
                                            <Box
                                                display="flex"
                                                justifyContent="space-between"
                                                alignItems="center"
                                            >
                                                <Typography variant="subtitle1">
                                                    {i18n.t('automation.automaticZClosing')}
                                                </Typography>
                                                <Switch
                                                    color="primary"
                                                    checked={values.isEnabled}
                                                    onChange={(e) =>
                                                        setFieldValue(
                                                            'isEnabled',
                                                            (e.target as HTMLInputElement).checked
                                                        )
                                                    }
                                                />
                                            </Box>
                                            <Typography variant="body2" color="textSecondary">
                                                {i18n.t('automation.automaticZClosingDescription')}
                                            </Typography>
                                            <Divider className={classes.automationDivider} />
                                            <Box
                                                display="flex"
                                                justifyContent="space-between"
                                                alignItems="center"
                                            >
                                                <Box>
                                                    <Typography variant="subtitle2">
                                                        {i18n.t('automation.generationTime')}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                    >
                                                        {i18n.t(
                                                            values.isEnabled
                                                                ? 'automation.generationTimeDescription'
                                                                : 'automation.generationTimeDisabledHelper'
                                                        )}
                                                    </Typography>
                                                </Box>
                                                <FormikTimePicker
                                                    name="generationTime"
                                                    required={values.isEnabled}
                                                    disabled={!values.isEnabled}
                                                    invalidDateMessage={i18n.t(
                                                        'automation.invalidTimeError'
                                                    )}
                                                    inputVariant="outlined"
                                                />
                                            </Box>
                                        </Box>
                                        <Box className={classes.automationRightPanel}>
                                            {preview ? (
                                                <>
                                                    <Typography
                                                        variant="overline"
                                                        color="textSecondary"
                                                    >
                                                        {i18n.t('automation.nextAutomaticZClosing')}
                                                    </Typography>
                                                    <Typography variant="h6">
                                                        {formatBusinessDay(
                                                            preview.nextGenerationDate,
                                                            language
                                                        )}
                                                    </Typography>
                                                    <Typography
                                                        variant="h4"
                                                        color="primary"
                                                        align="left"
                                                        style={{ marginTop: 10 }}
                                                    >
                                                        {formatGenerationTime(
                                                            preview.nextGenerationDate
                                                        )}
                                                    </Typography>
                                                    <Divider
                                                        className={classes.automationDivider}
                                                    />
                                                    <Box
                                                        display="flex"
                                                        justifyContent="space-between"
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            color="textSecondary"
                                                        >
                                                            {i18n.t('automation.businessDay')}
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {formatBusinessDay(
                                                                preview.nextGenerationBusinessDay,
                                                                language
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                </>
                                            ) : (
                                                <>
                                                    <Typography
                                                        variant="overline"
                                                        color="textSecondary"
                                                    >
                                                        {i18n.t('automation.automaticZClosingOff')}
                                                    </Typography>
                                                    <Typography variant="h6">
                                                        {i18n.t(
                                                            'automation.noAutomaticClosingScheduled'
                                                        )}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                    >
                                                        {i18n.t(
                                                            'automation.enableToGenerateReport'
                                                        )}
                                                    </Typography>
                                                </>
                                            )}
                                        </Box>
                                    </Box>
                                    <Box className={classes.automationNote}>
                                        <InfoIcon color="primary" fontSize="small" />
                                        <Typography variant="body2">
                                            {i18n.t('automation.manualClosingsNote')}
                                        </Typography>
                                    </Box>
                                </fieldset>
                            </Form>
                        </>
                    );
                }}
            </Formik>
        );
    }
);

AutomationTab.displayName = 'AutomationTab';

export default AutomationTab;
